import { useTranslation } from 'react-i18next';
import { useMembersQuery, useMeQuery } from '@platform/state/api';
import { useLabel } from '@platform/hooks/useLabel';
import { routes } from '@platform/core/lib/routes';
import { moduleNav } from '@platform/navigation';
import { appRoutes } from '../../shell/appRoutes';
import { moduleCount } from '../../shell/moduleCount';
import { usePendingInvitations } from '../../shell/usePendingInvitations';
import { useShellNav } from '../../shell/useShellNav';
import type { ISystemMenuGroup } from '../system/ISystemMenuGroup';
import { SystemMenu } from '../system/SystemMenu';
import type { IAdminMenuProps } from './IAdminMenuProps';
import { useWorkspaceRows } from './useWorkspaceRows';

/** Порядок модулів «Розробки платформи» як у прототипі v4 (ws.modules): життєвий цикл, моделі, впровадження, VS Code, бізнес-аналіз. */
const META_ORDER = ['alm', 'inf', 'imp', 'vsc', 'ba'];
const metaRank = (key: string) => (META_ORDER.includes(key) ? META_ORDER.indexOf(key) : META_ORDER.length);

/**
 * Ліве меню «Адміністрування» (прототип v4 sysGroups admin): «Адміністрування / Люди, доступ і платформа», «Огляд»; «Люди» — учасники,
 * запрошення (ті, що очікують), ролі й дозволи, глобальні ролі; «Платформа» — робочі простори (усі простори платформи, як плитка огляду), способи входу,
 * загальне; «Моніторинг» — журнал аудиту; «Розробка платформи» — модулі категорії «Мета» з лічильниками записів. Адміністратор платформи бачить
 * ще «Облікові записи» (після глобальних ролей) і «Демо-офіси спостерігача» (після робочих просторів) — рішення 5 етапу 7, прототип їх не має.
 */
export function AdminMenu({ ws }: IAdminMenuProps) {
  const { t } = useTranslation();
  const label = useLabel();
  const { catalog } = useShellNav(ws);
  const { data: members } = useMembersQuery(ws, { skip: !ws });
  const invitations = usePendingInvitations(ws);
  const spaces = useWorkspaceRows();
  const { data: me } = useMeQuery();
  const admin = me?.globalRole === 'Platform admin';
  const s = (key: string) => t(`sysPages.sections.${key}`);
  const at = (section: string) => appRoutes.admin(section);
  const meta = (catalog?.modules ?? []).filter((m) => m.installed && m.category === 'Meta' && moduleNav(m.key).listed).sort((a, b) => metaRank(a.key) - metaRank(b.key));
  const groups: ISystemMenuGroup[] = [
    { id: 'top', items: [{ key: 'overview', label: t('sysPages.menu.overview'), to: appRoutes.admin(), end: true }] },
    { id: 'people', title: t('sysPages.menu.people'), items: [
      { key: 'members', label: s('members'), to: at('members'), count: members?.length },
      { key: 'invitations', label: s('invitations'), to: at('invitations'), count: invitations },
      { key: 'roles', label: s('roles'), to: at('roles') },
      { key: 'global-roles', label: s('global-roles'), to: at('global-roles') },
      ...(admin ? [{ key: 'accounts', label: s('accounts'), to: at('accounts') }] : []),
    ] },
    { id: 'platform', title: t('sysPages.menu.platform'), items: [
      { key: 'workspaces', label: s('workspaces'), to: at('workspaces'), count: spaces.rows.length || undefined },
      ...(admin ? [{ key: 'observer-offices', label: s('observer-offices'), to: at('observer-offices') }] : []),
      { key: 'sign-in', label: s('sign-in'), to: at('sign-in') },
      { key: 'general', label: s('general'), to: at('general') },
    ] },
    { id: 'monitoring', title: t('sysPages.menu.monitoring'), items: [{ key: 'audit', label: s('audit'), to: at('audit') }] },
    // прототип: page:'module' без openModule — повернення до модуля в його поданні й пункті меню (як «Відкрити» налаштувань, рішення 6)
    { id: 'development', title: t('sysPages.menu.development'), items: meta.map((m) => ({ key: m.key, label: label(m.name), to: routes.moduleReturn(m.key), count: moduleCount(m) })) },
  ];
  return <SystemMenu menuKey="sys-admin" title={t('sysPages.admin')} sub={t('sysPages.adminMenuSub')} groups={groups} />;
}
